import { RHFStringInput } from "@impero-com/common/components/form/rhfFields/rhfStringInput";
import { Divider, Tooltip } from "@impero-com/ui";
import { Checkbox, Typography } from "antd";
import { Control, useController, useWatch } from "react-hook-form";
import { z } from "zod";

import { RiskDirectoryBlessedFieldIds } from "api/generated/model";
import { splitTemplateLiteralType } from "utils/split";

import { CopyableTag } from "./components/copyableTag";
import { StructureFieldCondition, conditionSchema } from "./fields/condition";
import { StructureFieldType, fieldTypeSchema } from "./fields/fieldType";
import { FormSchema } from "./schema";
import { SectionKey } from "./sections";
import { SubsectionKey } from "./subsections";
import * as theme from "./theme.css";

export type FieldKey = `${SubsectionKey}.fields.${number}`;
const fieldKeySchema = z.object({
    sectionIndex: z.coerce.number().min(0),
    subsectionIndex: z.coerce.number().min(0),
    fieldIndex: z.coerce.number().min(0),
});
type FieldKeyInfo = z.infer<typeof fieldKeySchema>;
export function parseFieldKey(fieldKey: FieldKey): FieldKeyInfo {
    const [_section, sectionIndex, _sub, subsectionIndex, _field, fieldIndex] =
        splitTemplateLiteralType(fieldKey, ".");
    return fieldKeySchema.parse({
        sectionIndex,
        subsectionIndex,
        fieldIndex,
    });
}

export const fieldSchema = z.object({
    fieldId: z.guid(),
    fieldType: fieldTypeSchema,
    requiredForAssessment: z.boolean(),
    name: z.string().trim().min(1).max(255),
    condition: z.nullable(conditionSchema),
});

export type RDStructureField = z.infer<typeof fieldSchema>;

interface StructureFieldProps {
    field: RDStructureField;
    control: Control<FormSchema>;
    sectionKey: SectionKey;
    fieldKey: FieldKey;
    blessedFieldIds: RiskDirectoryBlessedFieldIds;
}

export function StructureField({
    field,
    control,
    sectionKey,
    fieldKey,
    blessedFieldIds,
}: StructureFieldProps) {
    const { field: requiredForAssessmentField } = useController({
        name: `${fieldKey}.requiredForAssessment`,
        control,
    });

    const sectionLevel = useWatch({ name: `${sectionKey}.level`, control });

    const fieldType = useWatch({ name: `${fieldKey}.fieldType.type`, control });

    const condition = useWatch({ name: `${fieldKey}.condition`, control });
    const hasCondition = condition !== undefined && condition !== null;

    const formula = useWatch({
        name: `${fieldKey}.fieldType.formula`,
        control,
    });
    const hasFormula =
        formula !== undefined && formula !== null && formula !== "";

    let disableRequiredForAssessment = false;
    let requiredForAssessmentTooltip: string | undefined;
    if (!requiredForAssessmentField.value) {
        if (sectionLevel === "RiskDirectory") {
            disableRequiredForAssessment = true;
            requiredForAssessmentTooltip =
                "A RiskDirectory level field cannot be required for assessment";
        } else if (fieldType === "Heading") {
            disableRequiredForAssessment = true;
            requiredForAssessmentTooltip =
                "A field of type Heading cannot be required for assessment";
        } else if (hasCondition) {
            disableRequiredForAssessment = true;
            requiredForAssessmentTooltip =
                "A field with condition cannot be required for assessment";
        } else if (hasFormula) {
            disableRequiredForAssessment = true;
            requiredForAssessmentTooltip =
                "A field with formula cannot be required for assessment";
        }
    }
    const requiredForAssessmentCheckbox = (
        <Checkbox
            checked={requiredForAssessmentField.value}
            onChange={requiredForAssessmentField.onChange}
            value={requiredForAssessmentField.value}
            disabled={disableRequiredForAssessment}
        >
            Required for assessment
        </Checkbox>
    );

    return (
        <div className={theme.sectionContent}>
            <span className={theme.fieldIdTag}>
                <Typography.Text>ID</Typography.Text>
                <CopyableTag value={"$" + field.fieldId} />
            </span>
            <RHFStringInput
                name={`${fieldKey}.name`}
                control={control}
                label="Label"
                schema={fieldSchema.shape.name}
            />
            {disableRequiredForAssessment ? (
                <Tooltip title={requiredForAssessmentTooltip}>
                    {requiredForAssessmentCheckbox}
                </Tooltip>
            ) : (
                requiredForAssessmentCheckbox
            )}
            <StructureFieldCondition fieldKey={fieldKey} control={control} />
            <Divider />
            <StructureFieldType
                fieldKey={fieldKey}
                control={control}
                blessedFieldIds={blessedFieldIds}
            />
        </div>
    );
}
